var _vars = require('../../vars')._vars;
var generateQueryId = require('../../vars').generateQueryId; // MEDAIS-2052: Import generateQueryId
var api = require('./api');
var shared = require('../../shared');
var _html = require('../../html')._html;
var aiGating = require('../../ai-gating');
var scgHandler = require('../../scg-handler');
var ads = require('../../ads');

var citationTooltipState = {
    tooltipEl: null,
    activeMarker: null,
    previousFocus: null
};

// Cache for citation data received during streaming
var citationCache = {};

function escapeHtmlAttr(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function buildInlineCitationMarker(citationNumber, citationUrl) {
    var normalizedNumber = citationNumber !== null && citationNumber !== undefined ? String(citationNumber).trim() : '';
    if (!normalizedNumber) {
        return '';
    }
    var urlAttr = citationUrl ? ' data-citation-url="' + escapeHtmlAttr(citationUrl) + '"' : '';
    return '<span class="citation-circle inline-citation-marker" role="button" tabindex="0" aria-label="Open citation ' +
        escapeHtmlAttr(normalizedNumber) +
        '" data-citation-number="' +
        escapeHtmlAttr(normalizedNumber) +
        '"' +
        urlAttr +
        '>' +
        normalizedNumber +
        '</span>';
}

// Function to process hyperlinks in complete text strings [text](url) - only for non-numeric text
function processStaticHyperlinks(text) {
    if (!text || typeof text !== 'string') return text;
    
    // Process all [text](url) patterns, but skip numeric citations
    var textHyperlinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    return text.replace(textHyperlinkRegex, function(match, linkText, linkUrl) {
        // Skip if this is a numeric citation - let citation logic handle it
        if (/^\d+$/.test(linkText.trim())) {
            return match; // Return unchanged for numeric patterns
        }
        // Convert non-numeric text to clean hyperlink
        return '<a style="text-decoration:none; cursor:pointer;" href="' + linkUrl + '" onclick="event.preventDefault(); window.open(\'' + linkUrl + '\', \'_blank\');">' + linkText + '</a>';
    });
}

// Function to process streaming hyperlink conversion [text](url) - only for non-numeric text
function processStreamingHyperlinks(chunk, buffer) {
    var processedContent = '';
    var i = 0;
    
    while (i < chunk.length) {
        var char = chunk[i];
        
        switch (buffer.state) {
            case 'normal':
                if (char === '[') {
                    buffer.state = 'capturing_text';
                    buffer.textBuffer = '';
                } else {
                    processedContent += char;
                }
                break;
                
            case 'capturing_text':
                if (char === ']') {
                    // Check if captured text is numeric (citation pattern) - if so, skip hyperlink processing
                    var isNumericCitation = /^\d+$/.test(buffer.textBuffer.trim());
                    
                    if (isNumericCitation) {
                        // This is a citation [1], [2], etc. - output as regular text and let existing logic handle it
                        processedContent += '[' + buffer.textBuffer + ']';
                        buffer.state = 'normal';
                        buffer.textBuffer = '';
                    } else {
                        // Check if next character is '(' to start URL capture for non-numeric text
                        if (i + 1 < chunk.length && chunk[i + 1] === '(') {
                            buffer.state = 'capturing_url';
                            buffer.urlBuffer = '';
                            i++; // Skip the '(' character
                        } else {
                            // Need to wait for more chunks to see if '(' follows
                            buffer.state = 'waiting_for_paren';
                        }
                    }
                } else {
                    buffer.textBuffer += char;
                }
                break;
                
            case 'waiting_for_paren':
                if (char === '(') {
                    buffer.state = 'capturing_url';
                    buffer.urlBuffer = '';
                } else {
                    // Not a hyperlink pattern, output as regular text
                    processedContent += '[' + buffer.textBuffer + ']' + char;
                    buffer.state = 'normal';
                    buffer.textBuffer = '';
                }
                break;
                
            case 'capturing_url':
                if (char === ')') {
                    // Complete hyperlink found - convert to HTML (only for non-numeric text)
                    var linkText = buffer.textBuffer.trim();
                    var linkUrl = buffer.urlBuffer.trim();
                    if (linkUrl && linkText && !/^\d+$/.test(linkText)) {
                        processedContent += '<a style="text-decoration:none; cursor:pointer;" href="' + linkUrl + '" onclick="event.preventDefault(); window.open(\'' + linkUrl + '\', \'_blank\');" >' + linkText + '</a>';
                    } else {
                        // Invalid URL or numeric citation, output as regular text
                        processedContent += '[' + buffer.textBuffer + '](' + buffer.urlBuffer + ')';
                    }
                    
                    // Reset state
                    buffer.state = 'normal';
                    buffer.textBuffer = '';
                    buffer.urlBuffer = '';
                } else {
                    buffer.urlBuffer += char;
                }
                break;
        }
        i++;
    }
    
    return processedContent;
}

function getCitationDataForMarker(marker) {
    if (!marker) {
        return null;
    }

    var citationNumber = (marker.getAttribute('data-citation-number') || marker.textContent || '').trim();
    if (!citationNumber) {
        return null;
    }

    var citationUrl = marker.getAttribute('data-citation-url') || '';
    var answerNode = marker.closest('.ai-search-answer');
    var referencesSection = answerNode ? answerNode.querySelector('.references-section') : null;
    var referenceItem = null;

    if (referencesSection) {
        referenceItem = referencesSection.querySelector('.reference-item[data-citation-number="' + citationNumber + '"]');
        if (!referenceItem) {
            var referenceNodes = referencesSection.querySelectorAll('.reference-item');
            referenceNodes.forEach(function(node) {
                if (referenceItem) {
                    return;
                }
                var circle = node.querySelector('.citation-circle');
                if (circle && (circle.textContent || '').trim() === citationNumber) {
                    referenceItem = node;
                }
            });
        }
    }
    
    // If reference item exists in DOM, return full data from DOM
    if (referenceItem) {
        var referenceLink = referenceItem.querySelector('a[href]');
        var titleEl = referenceItem.querySelector('.reference-title-text');
        var metadataEl = referenceItem.querySelector('.reference-meta');
        var resolvedUrl = referenceLink ? referenceLink.getAttribute('href') : citationUrl;
        var titleHtml = titleEl ? titleEl.innerHTML : '';
        var metadataHtml = metadataEl ? metadataEl.innerHTML : '';

        return {
            citationNumber: citationNumber,
            citationUrl: resolvedUrl || '#',
            titleHtml: titleHtml,
            metadataHtml: metadataHtml,
            referencesSection: referencesSection,
            referenceItem: referenceItem,
            isReferenceReady: true
        };
    }
    // If reference item not in DOM yet, check cache for citation data received during streaming
    if (citationCache[citationNumber]) {
        return citationCache[citationNumber];
    }
    
    // No data available - return null
    return null;
}

function buildCitationTooltipMarkup(citationData) {
    var disableViewFullReference = _vars.pending || !citationData.isReferenceReady;
    var viewFullReferenceAttrs = disableViewFullReference
        ? ' type="button" class="citation-tooltip-action view-reference is-disabled" disabled aria-disabled="true"'
        : ' type="button" class="citation-tooltip-action view-reference"';

    var safeSourceUrl = citationData.citationUrl && citationData.citationUrl !== '#'
        ? citationData.citationUrl
        : '';

    return '<div class="citation-tooltip" role="dialog" aria-label="Citation ' + citationData.citationNumber + '">' +
        '<div class="citation-tooltip-caret"></div>' +
        '<div class="citation-tooltip-content">' +
        '<span class="citation-tooltip-number">' + citationData.citationNumber + '</span>' +
        '<div class="citation-tooltip-body">' +
        (safeSourceUrl
            ? '<a class="citation-tooltip-title" href="' + safeSourceUrl + '" target="_blank" rel="noopener noreferrer">' + citationData.titleHtml + '</a>'
            : '<span class="citation-tooltip-title disabled">' + citationData.titleHtml + '</span>') +
        (citationData.metadataHtml ? '<div class="citation-tooltip-metadata">' + citationData.metadataHtml + '</div>' : '') +
        '<div class="citation-tooltip-footer">' +
        '<button data-action="view-reference"' + viewFullReferenceAttrs + '>View full reference</button>' +
        (safeSourceUrl
            ? '<a class="citation-tooltip-action view-source" data-action="view-source" href="' + safeSourceUrl + '" target="_blank" rel="noopener noreferrer">View source</a>'
            : '<span class="citation-tooltip-action view-source is-disabled" aria-disabled="true">View source</span>') +
        '</div>' +
        '</div>' +
        '<button type="button" class="citation-tooltip-close" aria-label="Close citation tooltip"></button>' +
        '</div>' +
        '</div>';
}

function positionCitationTooltip(marker, tooltipEl) {
    if (!marker || !tooltipEl) {
        return;
    }

    var markerRect = marker.getBoundingClientRect();
    var tooltipRect = tooltipEl.getBoundingClientRect();
    var gap = 10;
    var edgeMargin = 12;
    var placement = 'bottom';
    var top = markerRect.bottom + gap;

    if (top + tooltipRect.height > window.innerHeight - edgeMargin) {
        var hasRoomAbove = markerRect.top - gap - tooltipRect.height >= edgeMargin;
        if (hasRoomAbove) {
            placement = 'top';
            top = markerRect.top - gap - tooltipRect.height;
        } else {
            top = Math.max(edgeMargin, window.innerHeight - edgeMargin - tooltipRect.height);
            if (window.innerHeight - markerRect.bottom < 80) {
                window.scrollBy(0, -80);
            }
        }
    }

    var left = markerRect.left + (markerRect.width / 2) - (tooltipRect.width / 2);
    left = Math.max(edgeMargin, Math.min(left, window.innerWidth - edgeMargin - tooltipRect.width));

    var caretLeft = markerRect.left + (markerRect.width / 2) - left;
    caretLeft = Math.max(14, Math.min(caretLeft, tooltipRect.width - 14));

    tooltipEl.style.top = Math.round(top) + 'px';
    tooltipEl.style.left = Math.round(left) + 'px';
    tooltipEl.style.setProperty('--citation-tooltip-caret-left', Math.round(caretLeft) + 'px');
    tooltipEl.setAttribute('data-placement', placement);
}

function closeCitationTooltip(restoreFocus) {
    if (citationTooltipState.activeMarker) {
        citationTooltipState.activeMarker.classList.remove('citation-tooltip-active');
    }

    if (citationTooltipState.tooltipEl && citationTooltipState.tooltipEl.parentNode) {
        citationTooltipState.tooltipEl.parentNode.removeChild(citationTooltipState.tooltipEl);
    }

    if (restoreFocus && citationTooltipState.previousFocus && typeof citationTooltipState.previousFocus.focus === 'function') {
        citationTooltipState.previousFocus.focus();
    }

    citationTooltipState.tooltipEl = null;
    citationTooltipState.activeMarker = null;
    citationTooltipState.previousFocus = null;
}

function openCitationTooltip(marker, shouldFocusCloseButton) {
    var focusCloseButton = shouldFocusCloseButton !== false;
    var citationData = getCitationDataForMarker(marker);
    if (!citationData) {
        closeCitationTooltip(false);
        return;
    }

    closeCitationTooltip(false);

    citationTooltipState.previousFocus = marker;
    citationTooltipState.activeMarker = marker;
    marker.classList.add('citation-tooltip-active');

    var tooltipLayer = document.createElement('div');
    tooltipLayer.className = 'citation-tooltip-layer';
    tooltipLayer.innerHTML = buildCitationTooltipMarkup(citationData);

    document.body.appendChild(tooltipLayer);
    citationTooltipState.tooltipEl = tooltipLayer;
    positionCitationTooltip(marker, tooltipLayer);

    var closeButton = tooltipLayer.querySelector('.citation-tooltip-close');
    if (focusCloseButton && closeButton) {
        closeButton.focus();
    }
}

function refreshActiveCitationTooltip() {
    if (!citationTooltipState.activeMarker || !document.contains(citationTooltipState.activeMarker)) {
        closeCitationTooltip(false);
        return;
    }
    openCitationTooltip(citationTooltipState.activeMarker, false);
}

function scrollToFullReference(marker) {
    var citationData = getCitationDataForMarker(marker);
    if (!citationData || !citationData.referencesSection || !citationData.referenceItem) {
        return;
    }

    var referencesSection = citationData.referencesSection;
    var referenceItemWrapper = referencesSection.querySelector('.reference-item-wrapper');
    if (referencesSection.classList.contains('references-section-collapsed')) {
        referencesSection.classList.remove('references-section-collapsed');
        if (referenceItemWrapper) {
            referenceItemWrapper.style.maxHeight = referenceItemWrapper.scrollHeight + 'px';
        }
    }

    citationData.referenceItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

function initializeCitationTooltipHandlers() {
    if (window._aiCitationTooltipHandlersInitialized) {
        return;
    }
    window._aiCitationTooltipHandlersInitialized = true;

    document.addEventListener('click', function(event) {
        var citationMarker = event.target.closest('.inline-citation-marker');
        var tooltipElement = citationTooltipState.tooltipEl ? citationTooltipState.tooltipEl.querySelector('.citation-tooltip') : null;
        if (citationMarker) {
            event.preventDefault();
            event.stopPropagation();
            openCitationTooltip(citationMarker);
            if (typeof wmdPageLink !== 'undefined') {
                var citationNumber = citationMarker.getAttribute('data-citation-number') || citationMarker.textContent.trim();
                wmdPageLink('ai-search_ref-ar-click-' + citationNumber, citationMarker);
            }
            return;
        }

        if (!tooltipElement) {
            return;
        }

        if (event.target.closest('.citation-tooltip-close')) {
            event.preventDefault();
            closeCitationTooltip(true);
            return;
        }

        if (event.target.closest('[data-action="view-reference"]')) {
            var actionBtn = event.target.closest('[data-action="view-reference"]');
            if (actionBtn.hasAttribute('disabled')) {
                event.preventDefault();
                return;
            }
            event.preventDefault();
            scrollToFullReference(citationTooltipState.activeMarker);
            closeCitationTooltip(false);
            return;
        }

        if (event.target.closest('[data-action="view-source"]') || event.target.closest('.citation-tooltip-title')) {
            if (typeof wmdPageLink !== 'undefined' && citationTooltipState.activeMarker) {
                var activeCitationNumber = citationTooltipState.activeMarker.getAttribute('data-citation-number') || citationTooltipState.activeMarker.textContent.trim();
                wmdPageLink('ai-search_ref-ar-click-' + activeCitationNumber, citationTooltipState.activeMarker);
            }
            return;
        }

        if (!event.target.closest('.citation-tooltip')) {
            closeCitationTooltip(false);
        }
    });

    document.addEventListener('keydown', function(event) {
        var citationMarker = event.target.closest('.inline-citation-marker');
        if (citationMarker && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            openCitationTooltip(citationMarker);
            return;
        }

        if (event.key === 'Escape' && citationTooltipState.tooltipEl) {
            event.preventDefault();
            closeCitationTooltip(true);
        }
    });

    window.addEventListener('resize', function() {
        if (citationTooltipState.tooltipEl && citationTooltipState.activeMarker) {
            positionCitationTooltip(citationTooltipState.activeMarker, citationTooltipState.tooltipEl);
        }
    });

    window.addEventListener('scroll', function() {
        if (citationTooltipState.tooltipEl && citationTooltipState.activeMarker) {
            positionCitationTooltip(citationTooltipState.activeMarker, citationTooltipState.tooltipEl);
        }
    }, true);

    // Add scroll listener for answer content container to dismiss tooltip on scroll
    // Use event delegation on document since .ai-search-content may not exist yet
    document.addEventListener('scroll', function(event) {
        var target = event.target;
        if (target && target.classList && target.classList.contains('ai-search-content')) {
            if (citationTooltipState.tooltipEl && citationTooltipState.activeMarker) {
                closeCitationTooltip(false);
            }
        }
    }, true);
}

// Helper function to truncate text with ellipsis
function truncateFollowUpText(textElement, originalText) {
    if (!textElement || !originalText) return;
    
    // Reset to original text first
    textElement.textContent = originalText;
    
    // Allow a brief moment for reflow
    setTimeout(function() {
        var lineHeight = parseFloat(getComputedStyle(textElement).lineHeight) || 24;
        var maxLines = 2;
        var maxHeight = lineHeight * maxLines;
        
        // Check if content is actually overflowing
        if (textElement.scrollHeight > textElement.clientHeight + 2) {
            textElement.classList.add('is-truncated');
            
            // Binary search to find the optimal text length that fits with ellipsis
            var low = 0;
            var high = originalText.length;
            var bestText = originalText;
            
            while (low < high) {
                var mid = Math.floor((low + high + 1) / 2);
                textElement.textContent = originalText.substring(0, mid) + '...';
                
                if (textElement.scrollHeight <= maxHeight) {
                    bestText = originalText.substring(0, mid) + '...';
                    low = mid;
                } else {
                    high = mid - 1;
                }
            }
            
            // Apply the best fit text with ellipsis
            textElement.textContent = bestText;
        } else {
            // Text fits, remove truncated class and show full text
            textElement.classList.remove('is-truncated');
            textElement.textContent = originalText;
        }
    }, 10);
}

function renderFollowUpQuestions(questions) {
    if (!questions || !Array.isArray(questions) || questions.length === 0) {
        return;
    }
    
    // Deduplicate questions and limit to 5
    var uniqueQuestions = [];
    var seen = {};
    
    questions.forEach(function(question) {
        var normalized = question.trim().toLowerCase();
        if (!seen[normalized] && uniqueQuestions.length < 5) {
            seen[normalized] = true;
            uniqueQuestions.push(question.trim());
        }
    });
    
    if (uniqueQuestions.length === 0) {
        return;
    }
    
    // Find the last answer container and insert follow-up questions after Ad 2 (or references/rating if no Ad 2)
    var answerNodes = document.querySelectorAll('.ai-search-answer.active');
    if (answerNodes.length > 0) {
        var lastAnswer = answerNodes[answerNodes.length - 1];
        
        // PROFCORE-34567: Find Ad 2 container (only exists on full-width page)
        var ad2Container = lastAnswer.querySelector('[id^="ai-search-ad2-container"]');
        var referencesSection = lastAnswer.querySelector('.references-section');
        var ratingElement = lastAnswer.querySelector('.ai-search-rating');
        
        // Generate follow-up questions HTML
        var followUpHtml = _html.followUpQuestions(uniqueQuestions);
        
        // Insert after Ad 2 if it exists (full-width page with ads)
        // Otherwise insert after references/rating (modal or full-width without ads)
        if (ad2Container) {
            // Full-width page: Insert follow-ups after Ad 2 to prevent moving the ad
            ad2Container.insertAdjacentHTML('afterend', followUpHtml);
        } else if (referencesSection) {
            // Modal OR full-width without ads: Insert after references
            referencesSection.insertAdjacentHTML('afterend', followUpHtml);
        } else if (ratingElement) {
            // Modal OR full-width without ads: Insert after rating if no references
            ratingElement.insertAdjacentHTML('afterend', followUpHtml);
        } else {
            // Fallback: If no rating or references, append to the answer
            lastAnswer.insertAdjacentHTML('beforeend', followUpHtml);
        }
    }
      // Attach click handlers to follow-up questions
    setTimeout(function() {
        // Get only the newly added follow-up questions (last answer's follow-ups)
        var answerNodes = document.querySelectorAll('.ai-search-answer.active');
        if (answerNodes.length === 0) return;
        
        var lastAnswer = answerNodes[answerNodes.length - 1];
        var followUpItems = lastAnswer.querySelectorAll('.follow-up-question-item');
        
        // Store references for resize handling
        var resizeHandlers = [];
        
        followUpItems.forEach(function(item, index) {
            // Remove existing listeners to avoid duplicates
            var newItem = item.cloneNode(true);
            if (item.parentNode) {
                item.parentNode.replaceChild(newItem, item);
            } else {
                console.warn('Parent node not found for follow-up question item');
                return;
            }
            
            // Get original text from data attribute
            var originalText = newItem.getAttribute('data-question');
            var textElement = newItem.querySelector('.follow-up-question-text');
            
            // Initial truncation check
            if (textElement && originalText) {
                truncateFollowUpText(textElement, originalText);
                
                // Create resize handler for this specific element
                var resizeHandler = function() {
                    truncateFollowUpText(textElement, originalText);
                };
                
                // Store handler reference
                resizeHandlers.push({
                    handler: resizeHandler,
                    element: textElement,
                    text: originalText
                });
            }
            
            newItem.addEventListener('click', function() {
                var question = this.getAttribute('data-question');
                if (question) {
                    shared.handleSearch(question);
                    if (typeof wmdPageLink !== 'undefined') {
                       wmdPageLink('ai-search_followup-' + (index + 1)); 
                    }
                }
            });
            
            // Add keyboard support
            newItem.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    var question = this.getAttribute('data-question');
                    if (question) {
                        shared.handleSearch(question);
                        wmdPageLink('ai-search_followup-' + (index + 1));
                    }
                }
            });
        });
        
        // Add window resize listener with debouncing
        var resizeTimeout;
        var handleResize = function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function() {
                resizeHandlers.forEach(function(item) {
                    if (item.element && item.text) {
                        truncateFollowUpText(item.element, item.text);
                    }
                });
            }, 100);
        };
        
        // Remove existing resize listener if any
        if (window._followUpResizeHandler) {
            window.removeEventListener('resize', window._followUpResizeHandler);
        }
        
        // Store and attach new resize listener
        window._followUpResizeHandler = handleResize;
        window.addEventListener('resize', handleResize);
        
    }, 150); // Increased timeout to ensure DOM is fully rendered
}

function initialize() {
    return api.generateToken().then(function(data){
        if(data && data.token) {
            _vars.token = 'Bearer ' + data.token;
            return true;
        }
        return false;
    }).catch(function(error) {
        console.log("Token generation failed");
        return false;
    });
}

function sendMessage(question) {
    // MEDAIS-2052: Reset queryId from previous query before generating new one
    // EXCEPT for special flows where queryId was already set:
    // 1. PDQ flows - queryId set in ai-search.js initialize()
    // 2. Modal prompt clicks - queryId set in bindui.js before msg-submit-clkc analytics
    // This ensures:
    // - PDQ: msg-pdq-submit and all subsequent calls use the SAME QUERYID
    // - Modal prompts: msg-submit-clkc and all subsequent calls use the SAME QUERYID
    // - Follow-up queries: each gets a fresh QUERYID
    // - QUERYID persists throughout entire query lifecycle (including post-render scroll events)
    
    // Only reset if NOT a PDQ flow AND no existing queryId (which would indicate modal prompt click)
    if (!_vars.isUrlPredefinedQuery && !_vars.isModalPromptClicked) {
        _vars.queryId = null;
    }
    
    // MEDAIS-2052: Generate new QUERYID for each query submission
    // Preserve existing queryId if:
    // 1. PDQ flow (isUrlPredefinedQuery) - queryId set in ai-search.js initialize()
    // 2. Modal prompt click - queryId set in bindui.js before msg-submit-clkc analytics
    // Otherwise, generate a fresh queryId for manual input or follow-up queries
    if (!_vars.queryId) {
        _vars.queryId = generateQueryId();
    }
    
    // Refresh PVID for normal (non-PDQ) flows only
    // For PDQ flows, GenPVID() is already called in handleSearch() before Ad 1 fires
    // This ensures all calls (Ad 1, msg-sent, Ad 2) use the same PVID
    // MEDAIS-1999: Skip GenPVID() here for PDQ flows to prevent double refresh
    if (typeof GenPVID !== 'undefined' && !_vars.isUrlPredefinedQuery) {
        GenPVID();
    }
    // Remove old analytics context to prevent value carryover from previous query
	if (_vars.ranOnce) {
		shared.removeAnalyticsContext();
	}
    
    // Reset query-specific attributes in state
    shared.resetQueryAttributes();
    
    // Clear citation cache for new query
    citationCache = {};
    
    // Set query word count for analytics
    shared.setQueryWordCount(question);
    
    // PROFCORE-34511: snapshot the A2A article generation this stream belongs to.
    // captureA2AArticleContext() bumps _vars.a2aStreamGeneration whenever the reader moves to a
    // different feed article. Closing the layer does NOT abort the fetch, so without this check a
    // stream started on the previous article keeps reading and writes into whatever is current -
    // rendering the previous article's answer into the new article's block and clobbering
    // _vars.conversationId / the analytics threadId with the previous article's thread.
    var streamGeneration = _vars.a2aStreamGeneration;
    function isStaleStream() {
        return _vars.a2aStreamGeneration !== streamGeneration;
    }

    // Initialize stream
    var startTime = Date.now();
    var answer = '';
    var titleStatus = '';
    var isReferences = false;
    var refTitle = '';
    var citationTitle = '';
    var isFirstRender = true;
    var relatedArticles =[];
    var refCnt = 0;
    var followUpQuestions = null; // Store follow-up questions for later rendering
    var summaryBullets = null; // Store summary bullets for Key Points Card
    // Snapshot suppression intent synchronously at dispatch time (see vars.js) so it's immune
    // to timing/races with any other query's streaming/render calls.
    var suppressKeyPoints = _vars.suppressKeyPointsOnce;
    _vars.suppressKeyPointsOnce = false;
    _vars.taskId = null;
    _vars.runId = null;
    var currQ = document.querySelector('.ai-search-question.current, .ai-search-asking-question.current');
    var firstWordReceived = false; // Flag to track if first word analytics has been fired
    var msgSentFired = false; // Flag to track if msg-sent has been fired
    var ad1Fired = false; // Flag to ensure Ad 1 fires at most once per stream session
    var metricsReceived = false; // Flag to track if metrics event has been received from API
    _vars.queryStreamStartPerformance = performance.now()
    
    // Helper function to fire msg-sent analytics
    function fireMsgSent(useDelay) {
        if (msgSentFired) {
            return;
        }
        
        var attrs = shared.getAnalyticsAttributes();
        
        // Only fire if we have threadId and runId (essential identifiers)
        if (attrs.threadId === 'no-val' || attrs.runId === 'no-val') {
            console.warn('[Dify] Cannot fire msg-sent: missing threadId or runId', attrs);
            return;
        }
        
        msgSentFired = true;
        
        // Function to execute the analytics call
        var executeAnalytics = function() {
            // (PVID refresh now handled at query trigger)
            shared.withAnalyticsContext('msg-sent', function() {
                // Fire wmdTrack first to set mmodule and mlink, then wmdPageview with PVID refresh
                if (typeof wmdTrack !== 'undefined') {
                    wmdTrack('ai-search_msg-sent');
                }
                if (typeof wmdPageview !== 'undefined') {
                   // On article pages (e.g. deep dive on news articles), the refreshed PVID would
                   // otherwise make the host page re-fire its ad calls. Pass noAdRefresh=true so the
                   // PVID still updates for analytics but the host page ads are not refreshed.
                   wmdPageview('medscape.com/ai-search-msg-sent', currQ, _vars.isArticlePage === true);

                    // Verify what Adobe actually received
                    shared.verifyAdobeVariables();
                }
            }, {
                queryWordCount: attrs.queryWordCount,
                messageIntent: attrs.messageIntent,  // Can be 'no-val' if not received yet
                threadId: attrs.threadId,
                runId: attrs.runId,
                leadConceptId: attrs.leadConceptId  // Can be 'no-val'
            });
        };
        
        // Use delay only when called from metrics event, fire immediately before first word
        if (useDelay) {
            setTimeout(executeAnalytics, 50);
        } else {
            executeAnalytics();
        }
    }
    
    // Initialize streaming hyperlink conversion buffer
    var hyperlinkBuffer = {
        textBuffer: '',          // Buffer for text within [ ]
        urlBuffer: '',           // Buffer for URL within ( )
        state: 'normal',         // States: 'normal', 'capturing_text', 'capturing_url'
        pendingContent: ''       // Content waiting to be processed
    };
    try {
        if (!_vars.token) {
            return initialize().then(function(initialized) {
                if (!initialized) {
                    _vars.pending = false;
                    if (document.querySelector('.ai-search-question.current, .ai-search-asking-question.current') != null) {
                        document.querySelector('.ai-search-question.current, .ai-search-asking-question.current').classList.add('failed');
                        document.querySelectorAll('.ai-search-question.failed, .ai-search-asking-question.failed').forEach(function(failedQ) {
                            failedQ.removeEventListener('click', shared.handleRetry);
                            failedQ.addEventListener('click', shared.handleRetry);
                        });
                    }
                    currQ.classList.remove('loading');
                    document.querySelector('#ai-search-input-modal').disabled = false;
                    document.querySelector('.ai-search-stop').classList.remove('active');
                    return;
                }
                _vars.ranOnce = true;
                return sendMessage(question);
            });
        }
        
        clearTimeout(_vars.socketTimer);
        _vars.socketTimer = setTimeout(function() { 
            currQ.classList.remove('loading');
            document.querySelector('.ai-search-stop').classList.add('active');
            wmdPageLink('ai-search_msg-timeout');
            shared.scrollToBottom();
        }, 15000);
        _vars.searchResult.question = question;
        // sending the query
        api.sendEvent({
            "eventProperties":{
                "stepName":"question",
                "eventTimestamp": shared.getCurrentTimestamp(),
            }}
        );
		window.dispatchEvent(new CustomEvent('aiEvtQuestion', {
			detail: {
				qnum: document.querySelectorAll('.ai-search-question').length
			}
		}));
        api.initializeStream(question).then(function(response) {
			window.dispatchEvent(new CustomEvent('aiEvtApiRespond', {
				detail: {
					qnum: document.querySelectorAll('.ai-search-question').length
				}
			}));
            currQ.classList.remove('loading');
            if (!response.ok) {
                if(response.status === 401) {
                    clearTimeout(_vars.socketTimer);
                    _vars.token = null;
                    sendMessage(question);
                    return;
                } else {
                    throw new Error('Stream failed.');
                }
            }            // Don't show progress text initially - wait for stepText
            _vars.progressBTimer = setTimeout(function() { 
                shared.toggleProgressText('b');
            }, 2000);
            _vars.progressCTimer = setTimeout(function() { 
                shared.toggleProgressText('c');
            }, 4000);
            shared.scrollToBottom();
            // Note: msg-sent is now fired as pageview after receiving metrics event with lead_concept and message_intent
            // See metrics handling in stream processing below
            // Mark AI query as used for unknown users (after successful query submission)
	        aiGating.markAIQueryUsed();
            
            // For PDQ queries, Ad 1 is already fired from renderQuestion() using URL scg.
            // Mark ad1Fired so the metrics handler doesn't fire a duplicate.
            if (_vars.isUrlPredefinedQuery) {
                ad1Fired = true;
            }
            
            return response.body.getReader();
        })
        .then(function(reader) {
            if(!reader) {
                return;
            }
            // Superseded while the request was in flight - never start reading.
            if (isStaleStream()) {
                reader.cancel().catch(function() {});
                return;
            }
            const decoder = new TextDecoder();
            var buffer = '';

            function read() {
                reader.read().then(function(event)  {
                    // The reader switched articles mid-stream: this answer belongs to an article
                    // that is no longer on screen. Abandon quietly - no render, no thread_id write,
                    // no completion analytics - and release the connection.
                    if (isStaleStream()) {
                        reader.cancel().catch(function() {});
                        return;
                    }
                    if (event.done) {
                        if(startTime) {
                            var resTime = new Date().getTime() - startTime;
                        _vars.searchResult.responseTime = Math.round(resTime/1000).toString();
                        }
                        
                        // Set response word count and timing for analytics
                        shared.setResponseWordCount(_vars.searchResult.answer);
                        shared.setResponseTimeFirstToken();
                        
                        clearTimeout(_vars.socketTimer);
                        clearTimeout(_vars.progressBTimer);
                        clearTimeout(_vars.progressCTimer);
                        if(relatedArticles && relatedArticles.length > 0) {
                            var relArtHtml = '\n<ul>';
                            relatedArticles.forEach(function(link){
                                relArtHtml += '<li><a href="'+link+'" target="_blank">'+link+'</a></li>';
                            });
                            shared.renderAnswer(relArtHtml);
                        }
                          // Add rating buttons right after answer content
                        var answerBlocks = document.querySelectorAll('.ai-search-answer.active');
                        if (answerBlocks.length > 0) {
                            var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
                            var answerDiv = lastAnswerBlock.querySelector('.answer');
                            if (answerDiv) {
                                var existingRating = answerDiv.querySelector('.ai-search-rating');
                                if (!existingRating) {
                                    var answerContent = answerDiv.querySelector('.answer-content');
                                    if (answerContent) {
                                        answerContent.insertAdjacentHTML('afterend', _html.showRating());
                                    }
                                }
                            }
                        }
                        
                        if(_vars.searchResult.answer) {
                            // answer streaming end
                            _vars.queryStreamEndPerformance = Math.round(performance.now())
                            var eventData = {
                                "eventProperties": 
                                {
                                    "likeStatus": "na",
                                    "latencyMsFromQuery": shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                    "stepName":"streamingComplete",
                                    "eventTimestamp":shared.getCurrentTimestamp(),
                                }
                            }
                            api.sendEvent(eventData);
							window.dispatchEvent(new CustomEvent('aiEvtStreamComplete', {
								detail: {
									qnum: document.querySelectorAll('.ai-search-question').length
								}
							}));
                        }
                        _vars.pending = false;
                        document.querySelector('.ai-search-stop-btn-dify').classList.remove('active');
                        shared.toggleProgressText('remove');
                        document.querySelector('.ai-search-question.current, .ai-search-asking-question.current').classList.remove('current');
                        document.querySelector('#ai-search-input-modal').disabled = false;
                        var ratingNodes = document.querySelectorAll('.ai-search-rating');
						if (ratingNodes.length > 0) {
							ratingNodes[ratingNodes.length-1].classList.remove('hidden');
						}
                         // Process any accumulated markdown tables in the DOM
                        shared.processTablesInDOM();

                        // Refresh tooltip content/state after stream completion.
                        refreshActiveCitationTooltip();
                        
                        // Render follow-up questions at the very end, after references
                        if (followUpQuestions && Array.isArray(followUpQuestions) && followUpQuestions.length > 0) {
                            setTimeout(function() {
                                renderFollowUpQuestions(followUpQuestions);
                            }, 100);
                        }
                        
                        // Show Ad 2 after answer completes (full-width page only)
                        //waits for reference section to load in DOM first before trying to render ad to ensure proper placement
                        setTimeout(function() {
                            var wasAdDisplayed = ads.showAd2AfterAnswer();
                            // displaying the second ad
                            if(wasAdDisplayed){
                                api.sendEvent({
                                    "eventProperties": {
                                        "stepName": "second.ad.display",
                                        "latencyMsFromQuery": shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                        "latencyMsFromAnswerEnd": shared.recordLatencyMsFromQuery(_vars.queryStreamEndPerformance),
                                        "eventTimestamp":shared.getCurrentTimestamp(),
                                    }
                                })
                            }
                        }, 150);
                        // Use wrapper to set context, call analytics, and remove context
                        var attrs = shared.getAnalyticsAttributes();
                        shared.withAnalyticsContext('msg-rcvd', function() {
                            wmdPageLink('ai-search_msg-rcvd');
                     
							// Check and trigger gating after response completion
							aiGating.checkAndTriggerGatingAfterResponse();	
							
                        }, {
                            queryWordCount: attrs.queryWordCount,
                            responseWordCount: attrs.responseWordCount,
                            responseTimeFirstToken: attrs.responseTimeFirstToken,
                            messageIntent: attrs.messageIntent,
                            threadId: attrs.threadId,
                            runId: attrs.runId,
                            leadConceptId: attrs.leadConceptId
                        });
                        // Reset PDQ flag after stream completes so follow-up queries use API concept_id
                        if (_vars.isUrlPredefinedQuery) {
                            _vars.isUrlPredefinedQuery = false;
                        }

                        if (_vars.isModalPromptClicked) {
                            _vars.isModalPromptClicked = false;
                        }

                        if(attrs && attrs.runId && attrs.threadId) {
                            window.dispatchEvent(new CustomEvent('aiStreamComplete', {
                                detail: { 
                                    runId: attrs.runId,
                                    conversationId: attrs.threadId
                                }
                            }));
                        }
                        console.log("Stream complete");
                        return;
                    }

                    buffer += decoder.decode(event.value, { stream: true });

                    // Process each event line by line
                    const lines = buffer.split('\n');
                    buffer = lines.pop(); // Keep the last incomplete line in buffer

                    lines.forEach(function(line) {
                        if (line.startsWith('event: metadata')) {
                            // Next data line will contain metadata (thread_id, run_id)
                            return;
                        }
                        
                        if (line.startsWith('event: answer_metadata')) {
                            // Process next line as data for answer_metadata
                            return;
                        }
                        
                        if (line.startsWith('event: key_points')) {
                            // Process next line as data for key_points
                            return;
                        }
                        
                        if (line.startsWith('event: metrics')) {
                            // Process next line as data for metrics (contains lead_concept info)
                            return;
                        }
                        
                        if (line.startsWith('event: citation')) {
                            // Process next line as data for citation
                            return;
                        }
                        
                        if (line.startsWith('data:')) {
                            try {
                                const json = JSON.parse(line.substring(5).trim());
                                
                                // Handle metadata event - capture thread_id and run_id
                                if (json.thread_id && json.run_id) {
                                    shared.setMetadataAttributes({
                                        thread_id: json.thread_id,
                                        run_id: json.run_id
                                    });
 }
                                
                                // Handle metrics event - capture message_intent and lead_concept_id
                                if (json.metrics) {
                                    metricsReceived = true; // Mark metrics as received regardless of concept_id validity
                                    shared.setMetricsAttributes(json.metrics);
                                    
                                    // Fire msg-sent after receiving metrics (with messageIntent)
                                    fireMsgSent(true);
                                }
                                
                                // Handle citation event - store citation data in cache for tooltip display during streaming
                                if (json.citation && json.citation.identification_url) {
                                    var citationNumber = json.citation.identification_url;
                                    var titleText = json.citation.title_chunk || json.citation.title || 'Reference';
                                    var metadataText = json.citation.source_details || '';
                                    // Process markdown formatting in metadata
                                    if (metadataText) {
                                        metadataText = metadataText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
                                    }
                                    citationCache[citationNumber] = {
                                        citationNumber: citationNumber,
                                        citationUrl: json.citation.source_url || '#',
                                        titleHtml: titleText,
                                        metadataHtml: metadataText,
                                        isReferenceReady: false
                                    };
                                    return;
                                }
                                
                                // Store follow-up questions from answer_metadata for later rendering
                                if (json.follow_up_questions && Array.isArray(json.follow_up_questions)) {
                                    followUpQuestions = json.follow_up_questions;
                                    return;
                                }
                                
                                // Handle key_points event data
                                if (json.key_points && typeof json.key_points === 'string') {
                                    // First, process hyperlinks in the entire key points string
                                    var keyPointsText = processStaticHyperlinks(json.key_points.trim());
                                    if (keyPointsText) {
                                        // Split by newlines and filter out empty lines
                                        var bullets = keyPointsText.split('\n')
                                            .map(function(line) { return line.trim(); })
                                            .filter(function(line) { return line.length > 0 && line.startsWith('-'); })
                                            .map(function(line) { 
                                                var processedLine = line.substring(1).trim(); // Remove the leading '-'
                                                
                                                // Handle citations in format [number](url), [[number]](url), [number], or [[number]]
                                                // First handle double bracket citations [[number]](url)
                                                var doubleBracketWithUrlRegex = /\[\[(\d+)\]\]\(([^)]+)\)/g;
                                                processedLine = processedLine.replace(doubleBracketWithUrlRegex, function(match, citationNumber, citationUrl) {
                                                    return buildInlineCitationMarker(citationNumber, citationUrl);
                                                });

                                                // Then handle single bracket citations [number](url)
                                                var singleBracketWithUrlRegex = /\[(\d+)\]\(([^)]+)\)/g;
                                                processedLine = processedLine.replace(singleBracketWithUrlRegex, function(match, citationNumber, citationUrl) {
                                                    return buildInlineCitationMarker(citationNumber, citationUrl);
                                                });

                                                // Handle [[number]] (no url)
                                                var doubleBracketOnlyRegex = /\[\[(\d+)\]\]/g;
                                                processedLine = processedLine.replace(doubleBracketOnlyRegex, function(match, citationNumber) {
                                                    return buildInlineCitationMarker(citationNumber);
                                                });

                                                // Handle [number] (no url) - only if not already processed
                                                var singleBracketOnlyRegex = /\[(\d+)\]/g;
                                                processedLine = processedLine.replace(singleBracketOnlyRegex, function(match, citationNumber) {
                                                    return buildInlineCitationMarker(citationNumber);
                                                });

                                                return processedLine;
                                            });
                                        
                                        if (bullets.length > 0) {
                                            summaryBullets = bullets;
                                            // Immediately prepend key points to searchResult.answer for event logging
                                            if (_vars.searchResult) {
                                                var keyPointsForAnswer = bullets.map(function(bullet) {
                                                    // Strip HTML tags for cleaner logging while preserving text content
                                                    var cleanBullet = bullet.replace(/<[^>]*>/g, '');
                                                    return '' + cleanBullet;
                                                }).join('\n') + '\n\n';
                                                // Prepend immediately to the beginning of the answer field for event calls
                                                _vars.searchResult.answer = keyPointsForAnswer + _vars.searchResult.answer;
                                            }
                                        }
                                    }
                                    return;
                                }
                                
                                // Handle metrics event data for lead_concept extraction
                                if (json.metrics && json.metrics.concept_id) {
                                    // Validate concept_id value - must not be null, undefined, empty, or invalid
                                    var conceptId = json.metrics.concept_id;
                                    var trimmed = conceptId;
                                    
                                    // Handle string values - trim and remove quotes if present
                                    if (typeof conceptId === 'string') {
                                        trimmed = conceptId.trim();
                                        // Remove quotes if present and trim again
                                        if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
                                            (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
                                            trimmed = trimmed.slice(1, -1).trim();
                                        }
                                    }
                                    
                                    var isValidValue = conceptId && 
                                                      typeof conceptId === 'string' && 
                                                      trimmed !== '' && 
                                                      trimmed.length > 0 &&
                                                      !/^\s*$/.test(trimmed) &&  // Reject whitespace-only values
                                                      trimmed.toLowerCase() !== 'null' && 
                                                      trimmed.toLowerCase() !== 'none' && 
                                                      trimmed.toLowerCase() !== 'undefined' &&
                                                      trimmed !== '0' &&  // Reject string "0"
                                                      /^\d+$/.test(trimmed);  // Must be numeric (digits only)
                                    
                                    if (!isValidValue) {
                                        // IMPORTANT: Clear any previous lead_concept value when API returns invalid/None
                                        // This prevents ads from showing based on stale data from previous queries
                                        try {
                                            var bindui = require('../../bindui');
                                            if (bindui.clearLeadConcept && _vars.isFullPage) {
                                                bindui.clearLeadConcept();
                                            } else if (_vars.isFullPage) {
                                                // Fallback: manually clear if clearLeadConcept doesn't exist
                                                _vars.leadConcept = null;
                                            }
                                        } catch (error) {
                                            console.error('Error clearing lead concept:', error);
                                            // Fallback: manually clear
                                            _vars.leadConcept = null;
                                        }
                                        // Fire Ad 1 without SCG (invalid concept_id) — only if not already fired
                                        if (!ad1Fired) {
                                            ad1Fired = true;
                                            ads.showAd1OnQuery('api');
                                        }
                                        return;
                                    }
                                    if (_vars.isUrlPredefinedQuery && ads.hasValidUrlScg()) {
                                        // PDQ with valid URL scg: Ad 1 already fired from renderQuestion with URL scg.
                                        // URL scg is already set in PageMetadata/webmd.ads2 — no update needed.
                                        ad1Fired = true;
                                        return;
                                    }
                                    if (_vars.isUrlPredefinedQuery && !ads.hasValidUrlScg()) {
                                        // PDQ without valid URL scg: Ad 1 already fired from renderQuestion without SCG.
                                        // Now that API has returned a valid concept_id, update SCG so Ad 2 uses it.
                                        ad1Fired = true;
                                        try {
                                            if (_vars.isFullPage) {
                                                scgHandler.setLeadConcept(trimmed, 'api');
                                                try {
                                                    if (typeof wmdSetContext !== 'undefined') {
                                                        wmdSetContext("wb.ldcncptid", trimmed.toString() + 'p');
                                                    }
                                                } catch(e) {
                                                    console.error('[PDQ] Error setting wmdSetContext:', e);
                                                }
                                                shared.setMetricsAttributes({ concept_id: trimmed }, true);
                                            }
                                        } catch (error) {
                                            console.error('[PDQ] Error updating SCG from API for PDQ without URL scg:', error);
                                        }
                                        return;
                                    }
                                    try {
                                        if (_vars.isFullPage) {
                                            scgHandler.setLeadConcept(trimmed, 'api');
                                            wmdSetContext("wb.ldcncptid", trimmed.toString()+'p');
                                            
                                            // MEDAIS-1916: Update analytics state so msg-rcvd events show correct lead concept
                                            // For PDQ without valid scg, use forceSet=true to bypass the PDQ protection check
                                            // This ensures getAnalyticsAttributes() returns the API concept_id
                                            var shouldForceSet = _vars.isUrlPredefinedQuery && !ads.hasValidUrlScg();
                                            shared.setMetricsAttributes({ concept_id: trimmed }, shouldForceSet);
                                            // detecting intent and valid lead concept
                                            api.sendEvent({
                                                "eventProperties":{
                                                    "stepName":"intent.leadconcept.detected",
                                                    "latencyMsFromQuery":shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                                    "eventTimestamp":shared.getCurrentTimestamp(),
                                                }
                                            })
                                            // sending lead concept to DFP
                                            api.sendEvent({
                                                "eventProperties":{
                                                    "stepName":"leadconcept.sent",
                                                    "latencyMsFromQuery": shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                                        "eventTimestamp": shared.getCurrentTimestamp(),
                                                }
                                            })
                                            if (!ad1Fired) {
                                                ad1Fired = true;
                                                var wasFirstAdRendered = ads.showAd1OnQuery('api');
                                                if(wasFirstAdRendered){
                                                    api.sendEvent({
                                                        "eventProperties":{
                                                            "stepName": "first.ad.display",
                                                            "latencyMsFromQuery": shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                                            "eventTimestamp":shared.getCurrentTimestamp(),
                                                        }
                                                    })
                                                }
                                            }
                                        } else {
                                            // Not full-page: still mark ad1Fired so the isFirstRender
                                            // fallback does not fire a duplicate ad call.
                                            ad1Fired = true;
                                        }
                                    } catch (error) {
                                        console.error('[API] Error setting lead concept from API:', error);
                                    }
                                    return;
                                }
                                
                                // Handle metrics event with NO concept_id field at all
                                if ((json.metrics && !json.metrics.concept_id)) {
                                    if (!ad1Fired) {
                                        ad1Fired = true;
                                        ads.showAd1OnQuery('api');
                                    }
                                }

                                if(json.type && json.type === 'function') {
                                var stepTxt = json.step || (json.kwargs && json.kwargs.step);
                             
                                if(stepTxt){
                                    shared.updateProgressStep(stepTxt);
                                }
                                
                                // Check for Generation node completion for responseTimeToCompletion
                                if (json.kwargs && 
                                    json.kwargs.name === 'Generation' && 
                                    json.kwargs.status === 'succeeded') {
                                    if(startTime) {
                                        var completionTime = new Date().getTime() - startTime;
                                        _vars.searchResult.responseTime = Math.round(completionTime/1000).toString();
                                    }
                                }
                            }
                                if(json.type && json.type === 'ai') {
                                    if(!document.querySelector('.ai-search-more').classList.contains('active')) {
                                        var modalContent = document.querySelector('.ai-search-content');
                                        var isAtBottom = modalContent.scrollHeight - modalContent.scrollTop <= modalContent.clientHeight + 10;
                                        var spacerEl = document.querySelector('.temp-scroll-spacer');
                                        if (!isAtBottom && (!spacerEl || !_vars.pending)) {
                                            document.querySelector('.ai-search-content').removeEventListener('scroll', shared.handleDifyContentScroll);
                                            document.querySelector('.ai-search-more').classList.add('active');
                                            document.querySelector('.ai-search-content').addEventListener('scroll', shared.handleDifyContentScroll);
                                        }
                                    }
                                    // Process streaming hyperlinks first before adding to search result
                                    var hyperlinkProcessedContent = processStreamingHyperlinks(json.content, hyperlinkBuffer);
                                    _vars.searchResult.answer += hyperlinkProcessedContent;
                                    
                                    if(isFirstRender) {
                                        clearTimeout(_vars.socketTimer);
                                        if(startTime) {
											var resTime = new Date().getTime() - startTime;
											_vars.searchResult.responseTimeFirstToken = Math.round(resTime/1000).toString();        
                                        }
                                        document.querySelector('.ai-search-stop').classList.remove('active');
                                        clearTimeout(_vars.progressBTimer);
                                        clearTimeout(_vars.progressCTimer);
                                        shared.toggleProgressText('remove');
                                        document.querySelector('.ai-search-stop-btn-dify').classList.add('active');
                                        shared.hideHeader();
                                        
                                        // Fallback: if metrics event has not arrived by the time the first
                                        // AI content chunk is received, fire Ad 1 without SCG.
                                        // We use setTimeout(100) to defer the check until AFTER the entire
                                        // forEach loop for this chunk completes. This handles the case where
                                        // the metrics data: line and the first ai data: line arrive in the
                                        // same chunk but metrics is processed after ai in the loop order.
                                        if (!_vars.isUrlPredefinedQuery) {
                                            setTimeout(function() {
                                                if (!ad1Fired && !metricsReceived) {
                                                    ad1Fired = true;
                                                    ads.showAd1OnQuery('api');
                                                }
                                            }, 100);
                                        }
                                        
                                        // answer streaming start
                                        api.sendEvent({
                                            "eventProperties": {
                                                "stepName": "answer.start",
                                                "latencyMsFromQuery": shared.recordLatencyMsFromQuery(_vars.queryStreamStartPerformance),
                                                "eventTimestamp":shared.getCurrentTimestamp(),
                                            }
                                        })
                                    }
                                    
                                    // Track analytics for first word received
                                    if (!firstWordReceived && hyperlinkProcessedContent) {
                                        // Check if content has actual word (not just whitespace, markdown symbols, or HTML tags)
                                        var trimmedContent = hyperlinkProcessedContent.trim();
                                        // Remove common markdown/HTML formatting to check for actual content
                                        var contentWithoutFormatting = trimmedContent
                                            .replace(/[#*_\[\]()]/g, '') // Remove markdown symbols
                                            .replace(/<[^>]*>/g, '') // Remove HTML tags
                                            .replace(/\s+/g, ' ') // Normalize whitespace
                                            .trim();
                                        if (contentWithoutFormatting.length > 0) {
                                            firstWordReceived = true;
                                            
                                            // Fire msg-sent SYNCHRONOUSLY before first word if not already fired
                                            // This ensures msg-sent always fires even if metrics never arrive
                                            // Pass false to fire immediately without delay
                                            fireMsgSent(false);
                                            
                                            // Use wrapper to set context, call analytics, and remove context
                                            var attrs = shared.getAnalyticsAttributes();
                                            shared.withAnalyticsContext('msg-rcvd-frst-wrd', function() {
                                                wmdPageLink('ai-search_msg-rcvd-frst-wrd');
												window.dispatchEvent(new CustomEvent('aiEvtFirstToken', {
												  detail: {
													qnum: document.querySelectorAll('.ai-search-question').length
												  }
												}));
                                            }, {
                                                queryWordCount: attrs.queryWordCount,
                                                responseWordCount: attrs.responseWordCount,
                                                responseTimeFirstToken: _vars.searchResult.responseTimeFirstToken || attrs.responseTimeFirstToken,
                                                messageIntent: attrs.messageIntent,
                                                threadId: attrs.threadId,
                                                runId: attrs.runId,
                                                leadConceptId: attrs.leadConceptId
                                            });
                                        }
                                    }
                                    
                                    // Process the hyperlink-processed content for other formatting
                                    json.content = hyperlinkProcessedContent.replace(/#{2,}/g, '');
                                    // Handle citations in format [number](url), [[number]](url), [number], or [[number]]
                                    var processedContent = json.content;

                                    // First handle double bracket citations [[number]](url)
                                    var doubleBracketWithUrlRegex = /\[\[(\d+)\]\]\(([^)]+)\)/g;
                                    processedContent = processedContent.replace(doubleBracketWithUrlRegex, function(match, citationNumber, citationUrl) {
                                        return buildInlineCitationMarker(citationNumber, citationUrl);
                                    });

                                    // Then handle single bracket citations [number](url)
                                    var singleBracketWithUrlRegex = /\[(\d+)\]\(([^)]+)\)/g;
                                    processedContent = processedContent.replace(singleBracketWithUrlRegex, function(match, citationNumber, citationUrl) {
                                        return buildInlineCitationMarker(citationNumber, citationUrl);
                                    });

                                    // Handle [[number]] (no url)
                                    var doubleBracketOnlyRegex = /\[\[(\d+)\]\]/g;
                                    processedContent = processedContent.replace(doubleBracketOnlyRegex, function(match, citationNumber) {
                                        return buildInlineCitationMarker(citationNumber);
                                    });

                                    // Handle [number] (no url) - only if not already processed
                                    var singleBracketOnlyRegex = /\[(\d+)\]/g;
                                    processedContent = processedContent.replace(singleBracketOnlyRegex, function(match, citationNumber) {
                                        return buildInlineCitationMarker(citationNumber);
                                    });

                                    // Convert markdown bold and italic
                                    // Bold: **text**
                                    processedContent = processedContent.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
                                    // Italic: *text* (single asterisks that are not part of bold)
                                    processedContent = processedContent.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, function(m, pre, content){
                                        return pre + '<i>' + content + '</i>';
                                    });

                                    json.content = processedContent;
                                   
                                    if(!titleStatus && /\($/.test(json.content.trim())) {
                                        answer = json.content;
                                        titleStatus = 'check2';
                                    } else if(!titleStatus && /\*\*$/.test(json.content.trim())) {
                                        answer = json.content.replace(/\*\*/g, '');;
                                        titleStatus = 'success-bold';
                                    }
                                    //Commented this as this logic is no longer used but can be used in future if needed for reference
                                    //Also this condition is breaking the current flow and not showing the complete answer properly
                                    // } else if(!titleStatus && !isReferences && /\[/.test(json.content.trim())) {
                                    //     refTitle = '';
                                    //     citationTitle = json.content.trim().replace(/\(/g, '');
                                    //     titleStatus = 'success-citation-title';
                                    //  else if(!titleStatus && /\[/.test(json.content.trim())) {
                                    //     citationTitle = '';
                                    //     refTitle = json.content.trim().replace(/\[/g, '');
                                    //     titleStatus = 'success-url-title';
                                    // }
                                    else {
                                        if(titleStatus) {
                                            if(titleStatus === 'check1') {
                                                if(/^\.$/.test(json.content.trim())) {
                                                    answer += json.content;
                                                    titleStatus = 'check2';
                                                } else {
                                                    answer += json.content;
                                                    titleStatus = '';
                                                    if(!isReferences) {
                                                        shared.renderAnswer(answer, isFirstRender, (suppressKeyPoints ? null : summaryBullets));
                                                    }
                                                }
                                            } else if(titleStatus === 'check2') {
                                                if(/\*\*$/.test(json.content.trim())) {
                                                    answer = json.content.replace(/\*\*/g, '');
                                                    titleStatus = 'success-bold';
                                                } else if(/^http/.test(json.content.trim())) {
                                                    answer = json.content;
                                                    titleStatus = 'success-url';
                                                } else {
                                                    if(citationTitle) {
                                                        answer += citationTitle;
                                                        citationTitle = '';
                                                    }
                                                    answer += json.content;
                                                    titleStatus = '';
                                                    if(!isReferences) {
                                                        shared.renderAnswer(answer, isFirstRender, (suppressKeyPoints ? null : summaryBullets));
                                                    }
                                                    isFirstRender = false;
                                                }
                                            } else if(titleStatus === 'success-bold') {
                                                if(/\*\*/.test(json.content.trim())) {
                                                    titleStatus = '';
                                                    answer = '<b>'+answer+'</b>';
                                                    if(!isReferences) {
                                                        shared.renderAnswer(answer+json.content.replace(/\*\*/g, ''), isFirstRender, (suppressKeyPoints ? null : summaryBullets));
                                                    }
                                                    isFirstRender = false;
                                                    var boldPairCount = (json.content.match(/\*\*/g) || []).length;
                                                    if(boldPairCount > 1) {
                                                        answer = '';
                                                        titleStatus = 'success-bold';
                                                    }
                                                    if(isReferences && /\[/.test(json.content.trim())) {
                                                        refTitle = '';
                                                        titleStatus = 'success-url-title';
                                                    }
                                                } else if(json.content.trim() === 'References') {
                                                    isReferences = true;
                                                    answer += json.content;
                                                } else {
                                                    answer += json.content;
                                                }
                                            } else if(titleStatus === 'success-url') {
                                                if(/\)/.test(json.content.trim())) {
                                                    titleStatus = '';
                                                    if(!isReferences) {
                                                        if(citationTitle) {
                                                            shared.renderAnswer('&nbsp;<a href="'+answer.trim()+'" target="_blank">'+citationTitle.replace(/\[\[/g, '[').replace(/\]\]/g, ']') +'</a>&nbsp;'+json.content.replace(/\)|\*\*|\]|\[|\;/g, ''), isFirstRender);
                                                            citationTitle = '';
                                                            if(/\[/.test(json.content)) {
                                                                titleStatus = 'success-citation-title';
                                                            }
                                                        }
                                                    } else {
                                                        if(refTitle) {
                                                            shared.renderAnswer('<li><a onclick="wmdPageLink(\'ai-search_ref-ar-click-' + (refCnt + 1) + '\', this);" href="'+answer.trim()+'" target="_blank">'+refTitle+'</a></li>\n'+json.content.replace(/\)|\*\*/g, ''), isFirstRender);
                                                            refCnt++;
                                                            refTitle = '';
                                                        }
                                                    }

                                                    if(/\*\*/.test(json.content.trim())) {
                                                        answer = json.content.replace(/\)|\*\*/g, '');
                                                        titleStatus = 'success-bold';
                                                    }
                                                    answer = '';
                                                    isFirstRender = false;
                                                } else {
                                                    answer += json.content;
                                                }
                                            } else if(titleStatus === 'success-url-title') {
                                                if(isReferences && /\(/.test(json.content.trim())) {
                                                    titleStatus = 'check2';
                                                    refTitle += json.content.replace(/\]|\(/g, '');
                                                } else {
                                                    refTitle += json.content.replace(/\]|\[/g, '');
                                                }
                                            } else if(titleStatus === 'success-citation-title') {
                                                if(/\]/.test(json.content.trim())) {
                                                    if(/\]\($/.test(json.content.trim())) {
                                                        titleStatus = 'check2';
                                                        citationTitle += json.content.replace(/\(/g, '');
                                                    } else {
                                                        titleStatus = '';
                                                        citationTitle += json.content;
                                                    }
                                                } else {
                                                    citationTitle += json.content;
                                                }
                                            }
                                        } else {
                                            answer = json.content;
                                            if(citationTitle) {
                                                answer = citationTitle + answer;
                                                citationTitle = '';
                                            }
                                            if(!isReferences) {
                                                shared.renderAnswer(answer, isFirstRender, (suppressKeyPoints ? null : summaryBullets));
                                            }
                                            isFirstRender = false;
                                        }
                                    }
                                }                                // Handle source event with references
                                if(line.startsWith('event: source') || (json.sources && Array.isArray(json.sources))) {
                                    if(json.sources && json.sources.length > 0) {
                                        var answerBlocks = document.querySelectorAll('.ai-search-answer.active .answer');
                                        if(answerBlocks.length) {
                                            var lastAnswerBlock = answerBlocks[answerBlocks.length - 1];
                                            // Ensure rating buttons exist before adding references
                                            if (!lastAnswerBlock.querySelector('.ai-search-rating')) {
                                                var answerContent = lastAnswerBlock.querySelector('.answer-content');
                                                if (answerContent) {
                                                    answerContent.insertAdjacentHTML('afterend', _html.showRating());
                                                }
                                            }
                                            // Find or create references container after rating
                                            var ratingElement = lastAnswerBlock.querySelector('.ai-search-rating');
                                            var referencesContainer = lastAnswerBlock.querySelector('.references-section');
                                            if (!referencesContainer) {
                                                if (ratingElement) {
                                                    ratingElement.insertAdjacentHTML('afterend', _html.referencesHeading());
                                                    referencesContainer = lastAnswerBlock.querySelector('.references-section');
                                                }
                                            }
                                            // Append each reference item
                                            if (referencesContainer) {
                                                if (_vars.isFullPage === true) {
                                                    referencesContainer.classList.add('references-section-full-page'); // to only apply to full page
                                                    referencesContainer.classList.add('references-section-collapsed'); // by default collapsed
                                                } else {
                                                    referencesContainer.classList.remove('references-section-full-page');
                                                }
                                                var referenceItemsWrapper = referencesContainer.querySelector('.reference-item-wrapper')
                                                if (!referenceItemsWrapper) {
                                                    referenceItemsWrapper = document.createElement('div');
                                                    referenceItemsWrapper.className = 'reference-item-wrapper';
                                                    referencesContainer.appendChild(referenceItemsWrapper);
                                                }
                                                json.sources.forEach(function (source, index) {
                                                    var displayNumber = source.IDENTIFICATION_URL || (index + 1);
                                                    var sourceTitle = source.title_chunk || source.title || 'Reference';
                                                    var sourceUrl = source.source_url || '#';
                                                    var metadataText = (source.source_details && typeof source.source_details === 'string')
                                                        ? source.source_details.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
                                                        : '';
                                                    referenceItemsWrapper.insertAdjacentHTML('beforeend', _html.referenceItem(displayNumber, sourceTitle, sourceUrl, metadataText));
                                                    refCnt++;
                                                });
                                                refreshActiveCitationTooltip();
                                            }
                                            isFirstRender = false;
                                        }
                                        return;
                                    }
                                }
                                
                                if(!json.type && json.thread_id) {
                                    _vars.conversationId = json.thread_id;
                                    _vars.taskId = json.task_id || '';
                                    _vars.runId = json.run_id;
                                    clearTimeout(_vars.socketTimer);
                                    document.querySelector('.ai-search-stop').classList.remove('active');
                                    document.querySelector('.ai-search-stop-btn-dify').classList.add('active');
                                }
                            } catch (error) {
                                console.error("JSON Parsing Error:", error, line);
                            }
                        }
                    });

                    read(); // Continue reading the next chunk
                }).catch(function(error) {
                    // AbortError is expected when the user stops the query or closes the modal
                    // (controller.abort()); it's a deliberate cancel, not a read failure.
                    if (error && error.name === 'AbortError') { return; }
                    console.error("Read error:", error);
                });
            }
            read(); // Start reading the stream
        })
        .catch(function(error){
            // Deliberate stop/close (controller.abort()) is NOT a delivery failure: clean up
            // quietly, skipping the failed state and the msg-nt-dlvrd / ConvSearchError analytics.
            if (error && error.name === 'AbortError') {
                _vars.pending = false;
                clearTimeout(_vars.progressBTimer);
                clearTimeout(_vars.progressCTimer);
                clearTimeout(_vars.socketTimer);
                var abortStopEl = document.querySelector('.ai-search-stop');
                if (abortStopEl) { abortStopEl.classList.remove('active'); }
                var abortStopDifyEl = document.querySelector('.ai-search-stop-btn-dify');
                if (abortStopDifyEl) { abortStopDifyEl.classList.remove('active'); }
                var abortInputEl = document.querySelector('#ai-search-input-modal');
                if (abortInputEl) { abortInputEl.disabled = false; }
                return;
            }
            _vars.pending = false;
            // Reset PDQ flag on stream failure so follow-up queries don't inherit PDQ state
            if (_vars.isUrlPredefinedQuery) {
                _vars.isUrlPredefinedQuery = false;
            }
            clearTimeout(_vars.progressBTimer);
            clearTimeout(_vars.progressCTimer);
            wmdPageLink('ai-search_msg-nt-dlvrd', currQ);
            if (document.querySelector('.ai-search-question.current, .ai-search-asking-question.current') != null) {
                clearTimeout(_vars.socketTimer);
                document.querySelector('.ai-search-question.current, .ai-search-asking-question.current').classList.add('failed');
                document.querySelectorAll('.ai-search-question.failed, .ai-search-asking-question.failed').forEach(function(failedQ) {
                    failedQ.removeEventListener('click', shared.handleRetry);
                    failedQ.addEventListener('click', shared.handleRetry);
                });
            }
            currQ.classList.remove('loading');
            shared.toggleProgressText('remove');
            document.querySelector('#ai-search-input-modal').disabled = false;
            document.querySelector('.ai-search-stop').classList.remove('active');
            document.querySelector('.ai-search-stop-btn-dify').classList.remove('active');
            api.sendEvent({
                "eventName": "ConvSearchError",
                "eventProperties": 
                {
                    "errorType": "not-delivered"
                }
            });
            console.log('Stream failed');
        });
    } catch(error) {
        console.log(error);
    }  
}

function handleReferenceSectionToggle() {
    // skip for modal page
    if(_vars.isFullPage === false) return

    document.addEventListener('click', function(e){
        const referencesHeadingWrapper = e.target.closest('.references-heading-wrapper')
        if(!referencesHeadingWrapper) return

        const referencesSection = referencesHeadingWrapper.closest('.references-section')
        if(!referencesSection) return

        const referenceItemWrapper = referencesSection.querySelector('.reference-item-wrapper')
        if (!referenceItemWrapper) return

        if (referencesSection.classList.contains('references-section-collapsed')) {
            referenceItemWrapper.style.maxHeight = referenceItemWrapper.scrollHeight + "px"
            referencesSection.classList.remove('references-section-collapsed')
        } else {
            referenceItemWrapper.style.maxHeight = '0'
            referencesSection.classList.add('references-section-collapsed')
        }
    })
}

// immediately calling the function so that only one event listener is attached
handleReferenceSectionToggle()
initializeCitationTooltipHandlers()

module.exports = {
    sendQuery: sendMessage,
    renderFollowUpQuestions: renderFollowUpQuestions
};
